<script setup lang="ts">
import { VirtualBaseCarousel } from '#components'
import { isBedEquipment } from '~/helpers/house/bedrooms'
import type { ComponentExposed } from 'vue-component-type-helpers'
import type { HouseAreaEquipment, Locale } from 'lc-services/types'
import type { HouseDetails } from '~/types/house/types'
import type { VirtualBaseCarouselSwiperOptions } from '../VirtualBaseCarousel.vue'

const { bedrooms, house } = defineProps<{
  bedrooms: HouseAreaEquipment['areas']
  house: HouseDetails
}>()

const { locale, t } = useI18n<unknown, Locale>()
const { isMobile } = useBreakpoint()
const { query } = useRoute()
const { trackEvent } = useTracking()
const localePath = useLocalePath()

const cursor = ref(1)
const swiperRef = ref<ComponentExposed<
  typeof VirtualBaseCarousel<
    (typeof slides)['value'] extends Array<infer R> ? R : never
  >
> | null>(null)

const slides = computed(() =>
  bedrooms.flatMap((bedroom) => {
    const publicBedroomPhotos = (bedroom?.photos ?? []).filter((p) => !p.hidden)
    if (!publicBedroomPhotos.length) return []
    const firstBedroomPhoto = publicBedroomPhotos.reduce(
      (acc, c) => (acc.position < c.position ? acc : c),
      publicBedroomPhotos[0],
    )
    return {
      ...bedroom,
      bed: bedroom.equipments.find(isBedEquipment),
      hash: hashify(bedroom.name),
      name: bedroom.name[locale.value],
      src: firstBedroomPhoto.url,
    }
  }),
)
const maxLength = computed(() =>
  isMobile.value ? bedrooms.length : Math.ceil(bedrooms.length / 2),
)
/**
 * If all bedrooms have at least 1 photo, show the section
 * If only some bedrooms have at least 1 photo, hide the section
 * If no bedroom have at least 1 photo, hide the section
 */
const shouldShowSection = computed(
  () => bedrooms.length === slides.value.length,
)
const payloadTracking = computed(() => ({
  category: 'Product page',
  cluster_id: house.destination.cluster?.clusterId ?? '',
  cluster_name: house.destination.cluster?.name ?? '',
  country_name: house.destination.country?.name ?? '',
  destination_id: house.destinationId ?? '',
  destination_name: house.destination.name ?? '',
  product: buildTrackEventProduct({
    houseId: house.id,
    houseName: house.name,
    iconic: Boolean(house.iconicCollection),
    onlineBookingAvailable: Boolean(house.onlineReservation),
  }),
  source: 'product page',
}))

const onClickPrev = () => {
  if (!swiperRef.value) return
  swiperRef.value.slidePrev()
  if (cursor.value > 1) cursor.value--
}
const onClickNext = () => {
  if (!swiperRef.value) return
  swiperRef.value.slideNext()
  if (cursor.value < maxLength.value) cursor.value++
}

const swiperOptions: VirtualBaseCarouselSwiperOptions = {
  breakpoints: {
    576: {
      slidesPerGroup: 2,
      slidesPerView: 2,
    },
  },
  slidesPerView: 'auto',
  spaceBetween: 16,
}

onMounted(() => {
  if (shouldShowSection.value)
    trackEvent({
      event: 'discover_bedrooms_displayed',
      ...payloadTracking.value,
    })
})
</script>

<template>
  <section v-if="shouldShowSection" class="flex w-full flex-col gap-6">
    <div class="flex w-full items-center justify-between">
      <h2 class="m-0 grow text-2xl">
        {{ t('title') }}
      </h2>
      <div v-if="!isMobile && maxLength > 1" class="flex items-center gap-2">
        <span class="text-md"> {{ cursor }}/{{ maxLength }} </span>
        <div class="flex grow">
          <button
            class="p-0 text-gray-700"
            type="button"
            :disabled="cursor === 1"
            @click="onClickPrev"
          >
            <BaseIcon class="size-6" name="chevronLeft" />
          </button>
          <button
            class="p-0 text-gray-700"
            type="button"
            :disabled="cursor >= maxLength"
            @click="onClickNext"
          >
            <BaseIcon class="size-6" name="chevronRight" />
          </button>
        </div>
      </div>
    </div>

    <div class="sm:mx-auto sm:max-w-full">
      <VirtualBaseCarousel
        ref="swiperRef"
        class-name="rooms-photos-carousel"
        :slides="slides"
        :swiper-options="swiperOptions"
        :virtual="false"
        @slide-change-active-index="
          trackEvent({ event: 'discover_bedrooms_slide', ...payloadTracking })
        "
      >
        <template #slider-content="{ slide, slideIndex }">
          <NuxtLink
            class="relative flex flex-col items-center gap-2 hover:no-underline"
            :to="
              localePath({
                name: 'house-photos',
                params: { slug: house.slug[locale] },
                hash: `#${slide.hash}`,
                query,
              })
            "
            @click.passive="
              trackEvent({
                event: 'discover_bedrooms_click',
                ...payloadTracking,
              })
            "
          >
            <div class="w-full overflow-hidden">
              <BaseNuxtImg
                :alt="slide.name"
                :src="slide.src"
                height="230"
                width="350"
                sizes="80vw sm:520px"
              />
            </div>

            <div class="flex w-full flex-col text-gray-700">
              <p class="m-0 text-md font-bold">
                {{ t('room', { position: slideIndex }) }}
              </p>
              <p
                v-if="slide.bed?.quantity && slide.bed.name[locale]"
                class="m-0 text-md"
              >
                {{ slide.bed.quantity }} {{ slide.bed.name[locale] }}
                <span v-if="slide.bed.bedSize">({{ slide.bed.bedSize }})</span>
              </p>
              <p
                v-if="slide.view"
                class="mb-0 mt-2 w-fit rounded-full bg-secondary-100 px-2 py-1 text-center text-xs font-medium text-secondary-400"
              >
                {{ slide.view[locale] }}
              </p>
            </div>
          </NuxtLink>
        </template>
      </VirtualBaseCarousel>
    </div>
  </section>
</template>

<style scoped>
button:disabled {
  @apply text-gray-300;
}
:deep(.swiper-slide) {
  @apply w-3/4 sm:w-60;
}
</style>

<i18n lang="yaml">
fr:
  title: Découvrez les chambres
  room: Chambre {position}
en:
  title: Discover the rooms
  room: Room {position}
</i18n>
